import type { Tone } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';

export type PageStatusInput = { state?: string | null; publishedVersion?: string | null };
export type PageStatus = { key: string; tone: Tone; label: string; hint: string };

const humanize = (state: string) => {
  const words = state.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * What a page's state means for the live site, as one status lamp. A page
 * with no state has finished its workflow: it is live if it has a published
 * version, otherwise it is a draft. A declined deletion leaves the page as it
 * was, so it shows the same lamp with a note.
 */
export function pageStatus({ state, publishedVersion }: PageStatusInput): PageStatus {
  const live = !!publishedVersion;
  const base = (note?: string): PageStatus => (live
    ? { key: 'LIVE', tone: 'green', label: tr('Live', '已发布'), hint: note ? `On the site. ${note}` : 'On the site.' }
    : { key: 'DRAFT', tone: 'gray', label: tr('Draft', '草稿'), hint: note ? `Not published yet. ${note}` : 'Not published yet.' });

  switch (state || '') {
    // DRAFT is set by a declined request, but also when a Studio page is created and when a
    // pending deployment is cancelled, so it says nothing more than "no state".
    // PUBLISHED is Studio's name for a page its deployment put on the site: the same as a live Genesis page.
    case '':
    case 'DRAFT':
    case 'PUBLISHED':
      return base();
    case 'DELETE_DECLINED':
      return base('A deletion request was declined.');
    case 'UNDER_REVIEW':
      return { key: state, tone: 'amber', label: tr('In review', '审核中'), hint: 'Waiting for an approver.' };
    case 'PENDING_DEPLOY':
      return { key: state, tone: 'blue', label: tr('Ready to deploy', '待发布'), hint: 'Approved. Goes live with the next deployment.' };
    case 'SCHEDULED':
      return { key: state, tone: 'blue', label: tr('Scheduled', '已预约'), hint: 'Goes live with a scheduled deployment.' };
    case 'PENDING_DELETE':
      return { key: state, tone: 'red', label: tr('Deletion requested', '删除待审批'), hint: 'Waiting for an approver to confirm the deletion.' };
    case 'PENDING_REMOVAL':
      return { key: state, tone: 'red', label: tr('Withdrawal requested', '撤回待审批'), hint: 'Waiting for an approver to take it off the site.' };
    case 'REMOVED':
      return live
        ? { key: 'WITHDRAWING', tone: 'blue', label: tr('Withdrawing', '待撤回'), hint: 'Comes off the site with the next deployment.' }
        : { key: state, tone: 'gray', label: tr('Withdrawn', '已撤回'), hint: 'Taken off the site.' };
    case 'DELETED':
      return { key: state, tone: 'gray', label: tr('Deleted', '已删除'), hint: 'Deleted.' };
    default:
      // Studio pages can carry states of their own.
      return { key: state as string, tone: 'gray', label: humanize(state as string), hint: humanize(state as string) };
  }
}
